/**
 * Format a number as USD currency
 * @param value - The number to format
 * @param privacyMode - If true, returns masked value
 * @returns Formatted currency string or masked value
 */
export const formatCurrency = (value: number | null | undefined, privacyMode = false): string => {
  if (privacyMode) return '••••••';
  if (value === null || value === undefined || isNaN(value)) return '--';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format a number with specified decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @param privacyMode - If true, returns masked value
 * @returns Formatted number string or masked value
 */
export const formatNumber = (
  value: number | null | undefined,
  decimals = 2,
  privacyMode = false
): string => {
  if (privacyMode) return '••••••';
  if (value === null || value === undefined || isNaN(value)) return '--';

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format a percentage value
 * @param value - The percentage value
 * @param decimals - Number of decimal places (default: 1)
 * @param showSign - Whether to show + for positive values
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number | null | undefined,
  decimals = 1,
  showSign = true
): string => {
  if (value === null || value === undefined || isNaN(value)) return '--';

  const formatted = value.toFixed(decimals);
  if (showSign && value > 0) {
    return `+${formatted}%`;
  }
  return `${formatted}%`;
};

/**
 * Parse a currency string to a number
 * Removes currency symbols and commas
 * @param value - The currency string to parse
 * @returns Parsed number or 0 if invalid
 */
export const parseCurrency = (value: string): number => {
  const cleaned = value.toString().replace(/[$,]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Compact currency for chart axis labels ($954K, $1.2M).
 */
export const formatCompactCurrency = (
  value: number | null | undefined,
  privacyMode = false
): string => {
  if (privacyMode) return '••••';
  if (value === null || value === undefined || isNaN(value)) return '--';

  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    return `${sign}$${m >= 10 ? m.toFixed(1) : m.toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    const k = abs / 1_000;
    return `${sign}$${k >= 100 ? Math.round(k) : k.toFixed(1)}K`;
  }
  return `${sign}$${Math.round(abs)}`;
};

/**
 * Format a date for display
 * @param date - The date to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
): string => {
  return date.toLocaleDateString('en-US', options);
};

/**
 * Short dollar amounts for the retirement projection: $74.4K, $250K, $1.8M.
 * One decimal under $100K so derived figures can be checked from the screen;
 * wholeThousands keeps chart axis labels short.
 */
export const formatCompactDollars = (val: number, { wholeThousands = false } = {}): string => {
  if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) {
    const decimals = wholeThousands || val >= 1e5 ? 0 : 1;
    const thousands = Number((val / 1e3).toFixed(decimals));
    if (thousands >= 1000) return `$${(val / 1e6).toFixed(1)}M`;
    return `$${thousands}K`;
  }
  return `$${val.toFixed(0)}`;
};
